import React from 'react'
import RecursiveContainer from '../RecursiveContainer'

// "This happened at the same location I'm located" (design's Case details
// screen). This isn't a single backend field — it's a UI convenience that
// either copies the client's own city/state/zipcode into the incident
// location fields (caseCity/caseState/caseZipcode, both already existing,
// already-required fields), or reveals those same three fields for manual
// entry. Defaults to checked, since most incidents happen where the client
// lives; unchecking never loses anything already typed into the case
// fields, it just stops the auto-sync and lets you edit them directly.
//
// The checked state itself lives in formValues (field.name, "caseLocationSame")
// rather than local component state - this step gets unmounted/remounted as
// the wizard navigates, and local state used to have to re-guess "checked or
// not" from scratch on every remount by comparing caseCity/caseState/
// caseZipcode to the client's fields. That guess broke the moment the
// client edited their own city/state/zip on step 1 and came back: the case
// location (still holding its last synced value) no longer matched, so it
// read as "the user must have customized this" and both stopped syncing
// and showed a wrong unchecked box. Storing it in formValues avoids the
// guess entirely, and lets CreateFormLanding's onChangeUpdate keep the case
// location synced centrally the instant the client's own field changes -
// not just the next time this component happens to be mounted.
function CaseLocationSameCheckbox({ field, formValues, onChangeUpdate, errors, onBlurValidate }) {
  // Undefined covers both "never touched yet" and an older saved draft from
  // before this lived in formValues - both default to checked, same as always.
  const sameLocation = formValues[field.name] !== false;

  const toggle = (checked) => {
    onChangeUpdate({ target: { checked } }, field);
    if (checked) {
      // Snap the case location to the client's current one right away,
      // same as the centralized sync CreateFormLanding's onChangeUpdate
      // does whenever a client field changes while this stays checked.
      if (formValues.clientCity) onChangeUpdate({ target: { value: formValues.clientCity } }, field.cityField);
      if (field.stateField && formValues.clientState) onChangeUpdate({ target: { value: formValues.clientState } }, field.stateField);
      if (formValues.clientZipcode) onChangeUpdate({ target: { value: formValues.clientZipcode } }, field.zipField);
    }
  };

  // If a backend validation error lands on one of these three fields
  // (e.g. an invalid caseZipcode) while "same location" is still checked,
  // the fields would otherwise stay hidden and the highlighted error would
  // never be visible. Reveal them for editing in that case without
  // touching the checkbox itself - the sync only happens when the client's
  // own fields actually change (CreateFormLanding's onChangeUpdate, or
  // toggle() above), so this doesn't lose or overwrite anything.
  const hasLocationError = !!(
    errors?.[field.cityField?.name] ||
    errors?.[field.stateField?.name] ||
    errors?.[field.zipField?.name]
  );

  return (
    <div className="w-full font-plexsans">
      <label
        className={`flex items-start gap-2.5 p-3.5 rounded-lg border-[1.5px] cursor-pointer transition-colors ${
          sameLocation ? "border-tan bg-[rgba(138,98,49,.08)]" : "border-[rgba(20,33,61,.15)]"
        }`}
      >
        <input
          type="checkbox"
          checked={sameLocation}
          onChange={(e) => toggle(e.target.checked)}
          className="mt-0.5 h-4 w-4 accent-tan flex-none"
        />
        <div>
          <div className="font-medium text-navy text-[15px]">{field.label}</div>
          {sameLocation && (
            <p className="text-xs text-[rgba(20,33,61,.5)] mt-0.5 italic">{field.note}</p>
          )}
        </div>
      </label>

      {(!sameLocation || hasLocationError) && (
        <div className={`w-full mt-4 grid grid-cols-1 ${field.stateField ? "lg:grid-cols-3" : "lg:grid-cols-2"} gap-4`}>
          <RecursiveContainer field={field.cityField} formValues={formValues} onChangeUpdate={onChangeUpdate} errors={errors} onBlurValidate={onBlurValidate} />
          {field.stateField && <RecursiveContainer field={field.stateField} formValues={formValues} onChangeUpdate={onChangeUpdate} errors={errors} onBlurValidate={onBlurValidate} />}
          <RecursiveContainer field={field.zipField} formValues={formValues} onChangeUpdate={onChangeUpdate} errors={errors} onBlurValidate={onBlurValidate} />
        </div>
      )}
    </div>
  );
}

export default CaseLocationSameCheckbox
